<template>
  <!--
    Tarjeta de opción de la Ventanilla (horizontal, ancho completo).
    - `to`       → router-link (navegación interna)
    - `href`     → <a> externo (misma pestaña: continúa el flujo del trámite)
    - `clickable`→ <button> (la selección/navegación la decide el componente padre vía @click)
    - ninguno    → tarjeta no interactiva (aria-disabled) mientras no haya destino configurado
    Layout: en móvil apila ícono, título y viñetas; desde `md` el ícono ocupa la columna
    izquierda (20 %) y el título + viñetas la derecha (80 %).
    ARIA: en las variantes interactivas la raíz usa `aria-labelledby` (título) y, solo si hay
    `items`, `aria-describedby` (lista de viñetas). La variante deshabilitada solo expone
    `aria-disabled`. Los ids derivan de `_uid` y se mantienen estables entre re-renders.
  -->
  <component :is="tag" v-bind="bindings" :class="classes">
    <div class="flex justify-center items-center flex-shrink-0">
      <img :src="icon" alt="" aria-hidden="true" class="ventanilla-icon w-16 h-16 md:w-20 md:h-20" />
    </div>
    <div class="flex flex-col justify-center gap-2 flex-1 min-w-0">
      <span
        :id="titleId"
        class="block text-base md:text-lg font-semibold text-blue-dark leading-snug text-center md:text-left"
      >
        {{ label }}
      </span>
      <ul v-if="hasItems" :id="descId" class="list-disc pl-5 m-0 space-y-1 text-sm text-gray-700 text-left">
        <li v-for="(item, i) in items" :key="i">{{ item }}</li>
      </ul>
    </div>
  </component>
</template>

<script>
const BASE_CLASSES =
  "ventanilla-card group flex flex-col md:flex-row items-center gap-4 md:gap-8 w-full h-full bg-white rounded-xl shadow-md pl-5 pr-8 py-6 md:pl-6 md:pr-10 no-underline border transition-colors duration-200 text-left";
const INTERACTIVE_CLASSES =
  "transform transition hover:shadow-lg hover:border-blue focus:outline-none focus:ring-2 focus:ring-blue cursor-pointer";
const DISABLED_CLASSES = "opacity-50 cursor-not-allowed border-gray-200";

export default {
  name: "HomeOptionCard",
  props: {
    icon: { type: String, required: true },
    label: { type: String, required: true },
    to: { type: [String, Object], default: null },
    href: { type: String, default: null },
    items: { type: Array, default: () => [] },
    selected: { type: Boolean, default: false },
    clickable: { type: Boolean, default: false },
  },
  created() {
    // Ids por instancia (no reactivos): únicos entre tarjetas y fijos durante la vida del componente.
    this.titleId = `ventanilla-option-${this._uid}-title`;
    this.descId = `ventanilla-option-${this._uid}-desc`;
  },
  computed: {
    tag() {
      if (this.to) return "router-link";
      if (this.href) return "a";
      if (this.clickable) return "button";
      return "div";
    },
    hasItems() {
      return Array.isArray(this.items) && this.items.length > 0;
    },
    bindings() {
      const aria = {
        "aria-labelledby": this.titleId,
        ...(this.hasItems ? { "aria-describedby": this.descId } : {}),
      };
      if (this.to) return { to: this.to, ...aria };
      if (this.href) return { href: this.href, ...aria };
      if (this.clickable) return { type: "button", "aria-pressed": String(this.selected), ...aria };
      return { "aria-disabled": "true" };
    },
    classes() {
      const interactive = this.to || this.href || this.clickable;
      // Borde azul marcado al seleccionar; transparente en reposo (tarjeta blanca con sombra).
      const borderState = this.selected ? "border-blue" : "border-transparent";
      return [
        BASE_CLASSES,
        borderState,
        interactive ? INTERACTIVE_CLASSES : DISABLED_CLASSES,
      ];
    },
  },
};
</script>
